import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { DietIcon } from '@/components/ui/DietIcon';
import { DIET_TYPES, type DietType } from '@/types';

interface DietTypeSelectorProps {
  value: DietType | undefined;
  onChange: (dietType: DietType) => void;
  isVegan?: boolean;
  isJain?: boolean;
  onVeganChange?: (isVegan: boolean) => void;
  onJainChange?: (isJain: boolean) => void;
  error?: string;
  onFieldFocus?: () => void;
  className?: string;
}

/**
 * DietTypeSelector - Visual selector for Indian diet types
 * Shows color-coded diet type options with SVG icons and optional vegan/jain modifiers
 */
export const DietTypeSelector: React.FC<DietTypeSelectorProps> = ({
  value,
  onChange,
  isVegan = false,
  isJain = false,
  onVeganChange,
  onJainChange,
  error,
  onFieldFocus,
  className,
}) => {
  const handleDietTypeSelect = (dietType: DietType) => {
    onChange(dietType);
    if (onFieldFocus) {
      onFieldFocus();
    }
  };

  const handleVeganToggle = () => {
    if (onVeganChange) {
      onVeganChange(!isVegan);
    }
    if (onFieldFocus) {
      onFieldFocus();
    }
  };

  const handleJainToggle = () => {
    if (onJainChange) {
      onJainChange(!isJain);
    }
    if (onFieldFocus) {
      onFieldFocus();
    }
  };

  const showModifiers = value === 'veg';

  return (
    <div className={cn("space-y-4", className)}>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 required">
          Diet Type
        </label>
        
        <div className="grid grid-cols-3 gap-3">
          {Object.values(DIET_TYPES).map((dietType) => (
            <motion.button
              key={dietType.key}
              type="button"
              onClick={() => handleDietTypeSelect(dietType.key)}
              onFocus={onFieldFocus}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={cn(
                // Base styles
                "relative p-3 rounded-lg border-2 transition-all duration-200",
                "flex flex-col items-center text-center space-y-2",
                "hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2",
                
                // Selected state
                value === dietType.key
                  ? "border-current bg-current/10 focus:ring-current/20"
                  : "border-gray-200 bg-white hover:border-gray-300 focus:ring-primary/20",
                
                // Error state
                error && "border-red-300"
              )}
              style={{
                ...(value === dietType.key && {
                  borderColor: dietType.color,
                  backgroundColor: `${dietType.color}10`,
                }),
              }}
            >
              {/* Diet type icon */}
              <DietIcon 
                type={dietType.key === 'contains-eggs' ? 'eggs' : dietType.key} 
                size={32} 
              />
              
              {/* Diet type label */}
              <span className={cn(
                "text-xs font-medium",
                value === dietType.key ? "text-gray-900" : "text-gray-700"
              )}>
                {dietType.label}
              </span>
              
              {/* Selection indicator */}
              {value === dietType.key && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold"
                  style={{ backgroundColor: dietType.color }}
                >
                  ✓
                </motion.div>
              )}
            </motion.button>
          ))}
        </div>

        {/* Description for selected diet type */}
        {value && (
          <motion.p
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="text-xs text-gray-600 bg-gray-50 rounded-lg p-2"
          >
            {DIET_TYPES[value].description}
          </motion.p>
        )}
      </div>

      {/* Vegan/Jain modifiers for vegetarian items */}
      {showModifiers && (onVeganChange || onJainChange) && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="space-y-3 pl-4 border-l-2 border-gray-200"
        >
          <p className="text-sm font-medium text-gray-700">Additional Options</p>
          
          <div className="space-y-2">
            {onVeganChange && (
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isVegan}
                  onChange={handleVeganToggle}
                  onFocus={onFieldFocus}
                  className="w-4 h-4 checkbox-green border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-apple-green focus:border-apple-green"
                />
                <div className="flex items-center space-x-2">
                  <DietIcon type="vegan" size={16} />
                  <span className="text-sm text-gray-700">Can be prepared Vegan</span>
                </div>
              </label>
            )}
            
            {onJainChange && (
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isJain}
                  onChange={handleJainToggle}
                  onFocus={onFieldFocus}
                  className="w-4 h-4 checkbox-purple border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-600 focus:border-purple-600"
                />
                <div className="flex items-center space-x-2">
                  <DietIcon type="jain" size={16} />
                  <span className="text-sm text-gray-700">Can be prepared Jain</span>
                </div>
              </label>
            )}
          </div>
        </motion.div>
      )}
      
      {error && (
        <p className="text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};
